import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { conversationFrameDisplayTitle } from "@/lib/conversation-display";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { inputBarStandaloneMessages } from "@/i18n/messages/input-bar-standalone";
import { InputTargetRowIcon } from "./input-target-row-icon";
import type { StandaloneBarChip } from "./standalone-bar-draft";
import type { StandaloneBarPlaceholder } from "./standalone-bar-placeholder";
import { STANDALONE_CHIP_CLASS, STANDALONE_CHIP_ICON_CLASS, STANDALONE_CHIP_LABEL_CLASS } from "./standalone-bar-chip";
import { StandaloneBarChipGhost, StandaloneBarPlaceholderGhost } from "./standalone-bar-ghost";
import { chipGhostFits, placeChipGhost } from "./standalone-bar-ghost-layout";

/** The shared composer hooks need textarea behavior, not a textarea element. */
export interface ComposerFieldHandle {
  value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  setSelectionRange: (start: number, end: number) => void;
  focus: (options?: FocusOptions) => void;
}

function isChip(node: Node): boolean {
  return node instanceof HTMLElement && node.dataset.standaloneChip === "true";
}

function plainText(node: Node): string {
  if (isChip(node)) return "";
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node instanceof HTMLBRElement) return "\n";
  if (node instanceof HTMLElement && /^(DIV|P)$/.test(node.tagName) &&
    node.childNodes.length === 1 && node.firstChild instanceof HTMLBRElement) return "";
  let text = "";
  for (const child of node.childNodes) {
    if (child instanceof HTMLElement && /^(DIV|P)$/.test(child.tagName) && text && !text.endsWith("\n")) {
      text += "\n";
    }
    text += plainText(child);
  }
  return text;
}

function readValue(root: HTMLElement): string {
  return plainText(root).replace(/\u00a0/g, " ");
}

function selectedOffset(root: HTMLElement, edge: "start" | "end"): number {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return readValue(root).length;
  const range = selection.getRangeAt(0).cloneRange();
  if (!root.contains(range.commonAncestorContainer)) return readValue(root).length;
  const before = document.createRange();
  before.selectNodeContents(root);
  before.setEnd(
    edge === "start" ? range.startContainer : range.endContainer,
    edge === "start" ? range.startOffset : range.endOffset,
  );
  return plainText(before.cloneContents()).length;
}

function textPosition(root: HTMLElement, offset: number): [Node, number] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let last: Text | null = null;
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.parentElement?.closest("[data-standalone-chip]")) continue;
    last = node;
    const length = node.data.length;
    if (remaining < length) return [node, remaining];
    remaining -= length;
  }
  return last ? [last, last.data.length] : [root, root.childNodes.length];
}

function setCaret(root: HTMLElement, start: number, end: number): void {
  const [startNode, startOffset] = textPosition(root, start);
  const [endNode, endOffset] = textPosition(root, end);
  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function setCaretAfterChip(chip: HTMLElement): void {
  const range = document.createRange();
  range.setStartAfter(chip);
  range.collapse(true);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function caretImmediatelyAfterChip(root: HTMLElement, chip: StandaloneBarChip): boolean {
  if (selectedOffset(root, "start") !== selectedOffset(root, "end")) return false;
  if (selectedOffset(root, "start") !== chip.offset) return false;
  const selection = window.getSelection();
  const chipElement = root.querySelector("[data-standalone-chip]");
  if (!selection || !chipElement || selection.rangeCount === 0) return false;
  const chipEnd = document.createRange();
  chipEnd.setStartAfter(chipElement);
  chipEnd.collapse(true);
  return selection.getRangeAt(0).compareBoundaryPoints(Range.START_TO_START, chipEnd) >= 0;
}

function writeValue(root: HTMLElement, value: string, chip: StandaloneBarChip | null): void {
  const selection = window.getSelection();
  const ownsSelection = Boolean(selection?.anchorNode && root.contains(selection.anchorNode));
  const start = ownsSelection ? selectedOffset(root, "start") : value.length;
  const end = ownsSelection ? selectedOffset(root, "end") : start;
  const insertingChip = Boolean(chip && !root.querySelector("[data-standalone-chip]"));
  let chipElement: HTMLElement | null = null;
  if (!chip) {
    root.replaceChildren(document.createTextNode(value));
  } else {
    const offset = Math.min(Math.max(chip.offset, 0), value.length);
    const element = document.createElement("span");
    element.contentEditable = "false";
    element.dataset.standaloneChip = "true";
    element.dataset.standaloneChipFullLabel = chip.label;
    element.setAttribute("role", "group");
    element.setAttribute("aria-label", chip.label);
    element.dataset.testid = "standalone-bar-chip";
    element.className = STANDALONE_CHIP_CLASS;
    const iconHost = document.createElement("span");
    iconHost.dataset.standaloneChipIcon = "true";
    iconHost.className = STANDALONE_CHIP_ICON_CLASS;
    const label = document.createElement("span");
    label.dataset.standaloneChipLabel = "true";
    label.className = STANDALONE_CHIP_LABEL_CLASS;
    label.textContent = `${chip.label.startsWith("@") ? "@" : ""}${conversationFrameDisplayTitle(chip.label.replace(/^@/, ""))}`;
    element.append(iconHost, label);
    chipElement = element;
    root.replaceChildren(
      document.createTextNode(value.slice(0, offset)),
      element,
      document.createTextNode(value.slice(offset)),
    );
  }
  if (ownsSelection) {
    if (insertingChip && chipElement && start === end && start === chip?.offset) {
      setCaretAfterChip(chipElement);
    } else {
      setCaret(root, Math.min(start, value.length), Math.min(end, value.length));
    }
  }
}

export function StandaloneBarField({
  ref,
  value,
  chip,
  placeholder,
  disabled,
  onChange,
  onChipRemoved,
  onKeyDown,
  onPaste,
  onEngage,
}: {
  ref: Ref<ComposerFieldHandle>;
  value: string;
  chip: StandaloneBarChip | null;
  placeholder: StandaloneBarPlaceholder;
  disabled: boolean;
  onChange: (value: string, chipOffset?: number) => void;
  onChipRemoved: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLElement>) => void;
  onEngage: () => void;
}) {
  const t = useT(inputBarMessages);
  const tStandalone = useT(inputBarStandaloneMessages);
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chipGhostRef = useRef<HTMLDivElement>(null);
  const [chipIconHost, setChipIconHost] = useState<HTMLElement | null>(null);
  const [chipGhostShown, setChipGhostShown] = useState(false);
  const [chipGhostHeight, setChipGhostHeight] = useState(0);
  const [dismissedPrediction, setDismissedPrediction] = useState<string | null>(null);
  const [selectedPrediction, setSelectedPrediction] = useState<string | null>(null);
  const valueRef = useRef(value);
  const chipRef = useRef(chip);
  valueRef.current = value;
  chipRef.current = chip;
  // The routed chat's predicted next message. Never part of the value: both
  // layouts render outside the editable root, and only Tab inserts the text.
  // A transient disable (closing, sending) keeps it; the host's scope decides.
  const predictionKey = placeholder.prediction
    ? JSON.stringify([placeholder.prediction.icon.id, placeholder.prediction.text]) : null;
  const predictionDismissed = predictionKey !== null && dismissedPrediction === predictionKey;
  const prediction = predictionDismissed ? null : placeholder.prediction;
  const emptyGhost = prediction && !value && !chip ? prediction : null;
  const chipGhost = prediction && chip && !value.trim() ? prediction : null;
  // Tab and aria-description follow what is visible: an after-chip ghost with
  // no room (a long chip title at the bar's cap) takes neither.
  const ghostText = emptyGhost?.text ?? (chipGhostShown ? chipGhost?.text : null) ?? null;
  const selectPrediction = () => {
    if (disabled) return;
    setSelectedPrediction(predictionKey);
    const root = rootRef.current;
    if (!root) return;
    root.focus({ preventScroll: true });
    const selectedChip = root.querySelector<HTMLElement>("[data-standalone-chip]");
    if (selectedChip) setCaretAfterChip(selectedChip);
    else setCaret(root, 0, 0);
  };
  const writeFieldValue = useCallback((root: HTMLElement, next: string, nextChip: StandaloneBarChip | null) => {
    writeValue(root, next, nextChip);
    setChipIconHost(root.querySelector<HTMLElement>("[data-standalone-chip-icon]"));
  }, []);

  useImperativeHandle(ref, () => ({
    get value() { return rootRef.current ? readValue(rootRef.current) : valueRef.current; },
    set value(next: string) {
      if (rootRef.current) {
        writeFieldValue(rootRef.current, next, chipRef.current);
      }
      valueRef.current = next;
    },
    get selectionStart() { return rootRef.current ? selectedOffset(rootRef.current, "start") : 0; },
    get selectionEnd() { return rootRef.current ? selectedOffset(rootRef.current, "end") : 0; },
    setSelectionRange(start, end) {
      if (rootRef.current) setCaret(rootRef.current, start, end);
    },
    focus(options) { rootRef.current?.focus(options); },
  }), [writeFieldValue]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const currentChip = root.querySelector<HTMLElement>("[data-standalone-chip]");
    if (
      readValue(root) !== value ||
      root.querySelector("div,p,br") !== null ||
      (currentChip?.dataset.standaloneChipFullLabel ?? null) !== (chip?.label ?? null) ||
      (currentChip ? plainTextBefore(root, currentChip) : null) !== (chip?.offset ?? null)
    ) {
      writeFieldValue(root, value, chip);
    }
  }, [value, chip, writeFieldValue]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const ghost = chipGhostRef.current;
    const wrapper = wrapperRef.current;
    if (!root || !ghost || !wrapper) {
      // A ghost that returns starts hidden until it is placed and measured.
      setChipGhostShown(false);
      setChipGhostHeight(0);
      return;
    }
    const place = () => {
      const room = placeChipGhost(root, ghost, wrapper);
      const shown = room !== null && chipGhostFits(ghost, room);
      setChipGhostShown(shown);
      setChipGhostHeight(shown ? ghost.offsetTop + ghost.getBoundingClientRect().height + 4 : 0);
    };
    place();
    // Reserve the complete wrapped suggestion, including a new line after a
    // wide chip. Position depends on chip/width, never on reserved height.
    const observer = new ResizeObserver(place);
    observer.observe(wrapper);
    observer.observe(ghost);
    return () => observer.disconnect();
  }, [value, chip?.label, chip?.offset, chipGhost?.text]);

  return (
    <div ref={wrapperRef} className="relative grid min-w-0 flex-1" style={{ minHeight: chipGhostHeight || undefined }}>
      {!value && !chip ? (emptyGhost ? (
        <StandaloneBarPlaceholderGhost route={emptyGhost.fullRoute ?? placeholder.route}
          icon={emptyGhost.icon} text={emptyGhost.text} onSelect={selectPrediction} disabled={disabled} />
      ) : (
        <div className="pointer-events-none absolute inset-0 truncate py-1 text-appkit-body leading-5 text-muted" aria-hidden="true" data-testid="standalone-bar-placeholder">
          {predictionDismissed ? tStandalone("typeMessage") : <>{placeholder.lead}<span className="font-medium text-secondary">{placeholder.route}</span>{placeholder.tail}</>}
        </div>
      )) : null}
      <div
        ref={rootRef}
        role="textbox"
        aria-label={t("message")}
        aria-description={ghostText ?? undefined}
        aria-multiline="true"
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        {...{ autoComplete: "off" }}
        {...{ writingsuggestions: "false" }}
        data-testid="standalone-bar-field"
        onPointerDown={() => setSelectedPrediction(null)}
        onBlur={() => setSelectedPrediction(null)}
        className="scrollbar-none min-h-7 max-h-[168px] min-w-0 self-start overflow-y-auto whitespace-pre-wrap break-words bg-transparent py-1 text-appkit-body leading-5 text-primary outline-none [grid-area:1/1]"
        onInput={(event) => {
          setSelectedPrediction(null);
          const root = event.currentTarget;
          const next = readValue(root);
          const currentChip = root.querySelector<HTMLElement>("[data-standalone-chip]");
          if (chip && !currentChip) onChipRemoved();
          if (next !== value) { onChange(next, currentChip ? plainTextBefore(root, currentChip) : undefined); onEngage(); }
        }}
        onBeforeInput={(event) => {
          const native = event.nativeEvent as InputEvent;
          if (native.isComposing || !["insertParagraph", "insertLineBreak"].includes(native.inputType)) return;
          event.preventDefault();
          const root = event.currentTarget;
          const current = readValue(root);
          const start = selectedOffset(root, "start");
          const end = selectedOffset(root, "end");
          const afterChip = chip && caretImmediatelyAfterChip(root, chip);
          const next = `${current.slice(0, start)}\n${current.slice(end)}`;
          writeFieldValue(root, next, chip);
          setCaret(root, start + 1, start + 1);
          onChange(next, afterChip ? chip.offset : undefined);
          onEngage();
        }}
        onKeyDown={(event) => {
          const root = rootRef.current;
          if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Escape"].includes(event.key)) {
            setSelectedPrediction(null);
          }
          if ((event.key === "Backspace" || event.key === "Delete") && ghostText && !disabled &&
            !event.nativeEvent.isComposing && event.nativeEvent.keyCode !== 229 &&
            (!chip || selectedPrediction === predictionKey)) {
            event.preventDefault();
            event.stopPropagation();
            setDismissedPrediction(predictionKey);
            setSelectedPrediction(null);
            return;
          }
          if (event.key === "Backspace" && chip && root && caretImmediatelyAfterChip(root, chip)) {
            event.preventDefault();
            onChipRemoved();
            return;
          }
          if (event.key === "Tab" && ghostText && !disabled && root && !event.shiftKey &&
            !event.altKey && !event.ctrlKey && !event.metaKey && !event.nativeEvent.isComposing) {
            // Only a visible ghost on an editable field takes Tab; otherwise Tab
            // keeps its native behaviour.
            event.preventDefault();
            setSelectedPrediction(null);
            const next = chip ? `${value.slice(0, chip.offset)}${ghostText}` : ghostText;
            writeFieldValue(root, next, chip);
            setCaret(root, next.length, next.length);
            onChange(next, chip?.offset);
            onEngage();
            return;
          }
          onKeyDown(event);
        }}
        onPaste={(event) => {
          onPaste(event);
          if (event.defaultPrevented) return;
          const text = event.clipboardData.getData("text/plain");
          if (!text) return;
          event.preventDefault();
          const root = event.currentTarget;
          const current = readValue(root);
          const start = selectedOffset(root, "start");
          const end = selectedOffset(root, "end");
          const afterChip = chip && caretImmediatelyAfterChip(root, chip);
          const next = current.slice(0, start) + text + current.slice(end);
          writeFieldValue(root, next, chip);
          setCaret(root, start + text.length, start + text.length);
          onChange(next, afterChip ? chip.offset : undefined);
          onEngage();
        }}
      />
      {chipGhost ? <StandaloneBarChipGhost ref={chipGhostRef} text={chipGhost.text}
        shown={chipGhostShown} onSelect={selectPrediction} disabled={disabled} /> : null}
      {chip && chipIconHost && rootRef.current?.contains(chipIconHost)
        ? createPortal(<InputTargetRowIcon row={chip.row ?? undefined} size={12} />, chipIconHost)
        : null}
    </div>
  );
}

function plainTextBefore(root: HTMLElement, chip: HTMLElement): number {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEndBefore(chip);
  return plainText(range.cloneContents()).length;
}
